import MerchantLogo from '@/components/cashflow/MerchantLogo';
import useMerchantSearch from '@/components/cashflow/useMerchantSearch';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexRow } from '@/components/common/Flex';
import { useMutation, useQuery } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import {
    CreateMerchantDocument,
    MerchantDocument,
    MerchantFieldsFragment,
} from '@common/graphql/generated';
import React, { useEffect, useState } from 'react';

const useMerchantSelect = ({
    merchant,
    merchantId,
    showLogo = false,
}: {
    merchant?: MerchantFieldsFragment | null;
    merchantId?: string | null;
    showLogo?: boolean;
}) => {
    const { data: merchantQuery } = useQuery(MerchantDocument, {
        variables: { id: merchantId as string },
        skip: !merchantId,
    });

    const [merchantValue, setMerchantValue] = useState<StringOption | null>(
        merchant ? getMerchantOption(merchant, showLogo) : null
    );

    const [createMerchant] = useMutation(CreateMerchantDocument);
    const { loadOptions } = useMerchantSearch();

    // When the merchant query resolves, update the merchant value stored in component state
    useEffect(() => {
        if (!merchantQuery?.merchant) return;

        setMerchantValue(getMerchantOption(merchantQuery.merchant, showLogo));
    }, [showLogo, merchantQuery?.merchant]);

    // When the merchant changes, update the merchant value stored in component state
    useEffect(() => {
        if (!merchant) return;

        setMerchantValue(getMerchantOption(merchant, showLogo));
    }, [showLogo, merchant]);

    const handleCreateOption = async (name: string) => {
        // Slight hack to update the merchant value immediately to show the new merchant in the select control
        setMerchantValue(getMerchantOption({ name, id: '' }, showLogo));

        const result = await createMerchant({
            variables: {
                name,
            },
        });

        if (result.data?.createMerchant?.merchant?.id) {
            // Update the dropdown menu with the actual merchant
            setMerchantValue(
                getMerchantOption(
                    result.data?.createMerchant?.merchant,
                    showLogo
                )
            );

            return result.data.createMerchant.merchant;
        }
    };

    const handleChange = (option: StringOption) => {
        setMerchantValue(option);
    };

    return {
        handleLoadOptions: loadOptions,
        handleChange,
        handleCreateOption,
        merchantValue,
    };
};

export const getMerchantOption = (
    merchant:
        | Pick<MerchantFieldsFragment, 'name' | 'id' | 'logoUrl'>
        | undefined
        | null,
    showLogo: boolean
) =>
    merchant
        ? {
              label: showLogo ? (
                  <FlexRow gap={2} align="center">
                      <MerchantLogo merchant={merchant} />
                      <Text mb="2px">
                          {merchant?.name || 'Unknown merchant'}
                      </Text>
                  </FlexRow>
              ) : (
                  merchant.name
              ),
              value: merchant.id,
          }
        : null;

export default useMerchantSelect;
